import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtClock } from '../lib/format.js';
import { kioskApi } from '../lib/kiosk-api.js';
import { KnowledgeDripCard } from './KnowledgeDripCard.jsx';

// ─── Kiosk Clock view (Round 38) ──────────────────────────────────────────
// Three states: empty (waiting for PIN), punched-out (Clock in), punched-in
// (shows elapsed + Break / End break / Clock out). PIN clears after every
// successful action so the next staffer steps up to a clean screen.
export function KioskClockView({ session }) {
  const locKey = session.location.key;
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Knowledge Drip: the post-punch moment. Shown after a successful clock-in,
  // never blocking — the card self-hides when drip is off or already seen.
  const [showDrip, setShowDrip] = useState(false);

  // The kiosk session user is the punching user — no PIN re-entry. The
  // server's resolveKioskPunchUser falls back to the session actor when
  // no PIN is in the body.
  async function fetchStatus() {
    setErr('');
    try {
      const r = await kioskApi('POST', `/api/kiosk/${locKey}/timeclock/status`, {}, session.token);
      setState(r);
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      setErr(m); setState(null);
    }
  }
  useEffect(() => { fetchStatus(); }, []);

  async function action(path, extra = {}) {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await kioskApi('POST', `/api/kiosk/${locKey}/timeclock/${path}`, { ...extra }, session.token);
      const name = r?.user?.name || session.user?.name || 'You';
      if (path === 'punch-in') {
        const t = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        setMsg(`✅ ${name} clocked in at ${t}`);
        setShowDrip(true);
      } else if (path === 'punch-out') {
        setMsg(`✅ ${name} clocked out`);
      } else if (path === 'break/start') {
        setMsg(`☕ ${name} on break`);
      } else if (path === 'break/end') {
        setMsg(`✅ ${name} back from break`);
      }
      await fetchStatus();
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      setErr(m);
    } finally { setBusy(false); }
  }

  if (!state) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, paddingTop: 24 }}>
        {err
          ? <div style={{ background: '#fee', padding: 12, borderRadius: 6, color: '#b91c1c', fontSize: 13 }}>{err}</div>
          : <div style={{ fontSize: 14, color: '#757575' }}>Loading…</div>}
      </div>
    );
  }

  const open = state.open_punch;
  const onBreak = state.on_break;
  const next = state.next_shift;
  const elapsed = open ? Math.max(0, Math.round((Date.now() - new Date(open.clock_in_at).getTime()) / 60000)) : 0;
  const eh = Math.floor(elapsed / 60), em = elapsed % 60;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, paddingTop: 8 }}>
      <div style={{ fontSize: 12, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hello</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#212121' }}>{state.user.name}</div>
      {open ? (
        <>
          <div style={{ fontSize: 13, color: onBreak ? '#92400e' : '#16a34a', fontWeight: 600 }}>
            {onBreak
              ? `On break since ${new Date(state.current_break.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
              : `Clocked in · ${eh}h ${em}m so far`}
          </div>
          {!onBreak && (
            <>
              <button onClick={() => action('punch-out')} disabled={busy}
                style={{ minWidth: 240, padding: '20px 24px', fontSize: 18, fontWeight: 700, fontFamily: 'inherit',
                          background: '#dc2626', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer' }}>
                {busy ? 'Working…' : 'Clock out'}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => action('break/start', { kind: 'paid_10' })} disabled={busy}
                  style={{ padding: '12px 18px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                            background: '#fff', color: '#a16207', border: '1px solid #fde68a', borderRadius: 8, cursor: 'pointer' }}>
                  ☕ Paid 10 min
                </button>
                <button onClick={() => action('break/start', { kind: 'unpaid_30' })} disabled={busy}
                  style={{ padding: '12px 18px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                            background: '#fff', color: '#7c2d12', border: '1px solid #fdba74', borderRadius: 8, cursor: 'pointer' }}>
                  🍽 Unpaid 30 min
                </button>
              </div>
            </>
          )}
          {onBreak && (
            <button onClick={() => action('break/end')} disabled={busy}
              style={{ minWidth: 240, padding: '20px 24px', fontSize: 18, fontWeight: 700, fontFamily: 'inherit',
                        background: 'var(--forest, #1B2B1B)', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer' }}>
              {busy ? 'Working…' : 'End break'}
            </button>
          )}
        </>
      ) : (
        <>
          {next ? (
            <div style={{ fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
              Scheduled today: {fmtClock(next.start_time)}–{fmtClock(next.end_time)}{next.role ? ` · ${next.role}` : ''}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#9e9e9e', fontStyle: 'italic' }}>No shift scheduled today — punch will be unscheduled.</div>
          )}
          <button onClick={() => action('punch-in')} disabled={busy}
            style={{ minWidth: 240, padding: '20px 24px', fontSize: 20, fontWeight: 700, fontFamily: 'inherit',
                      background: '#16a34a', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer' }}>
            {busy ? 'Working…' : 'Clock in'}
          </button>
        </>
      )}
      {err && <div style={{ background: '#fee', padding: 8, borderRadius: 6, color: '#b91c1c', fontSize: 13 }}>{err}</div>}
      {msg && <div style={{ background: '#dcfce7', padding: 8, borderRadius: 6, color: '#166534', fontSize: 13, fontWeight: 600 }}>{msg}</div>}
      {showDrip && (
        <KnowledgeDripCard mode="kiosk" session={session} onDone={() => setShowDrip(false)} />
      )}
    </div>
  );
}
